import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App'
import ErrorBoundary from './ErrorBoundary'
import './styles.css'

// ── Android safe-area insets ──────────────────────────────────────────────────
// On Android 15+ (API 35+) the OS forces edge-to-edge: the WebView draws behind
// the system status bar and navigation bar. Android WebView does NOT populate
// env(safe-area-inset-*) like iOS WKWebView does, so we read the real pixel
// heights via a JavascriptInterface injected by MainActivity and apply them as
// CSS custom properties synchronously — before React renders anything.
// This is timing-independent: JS reads when IT is ready, no push-timing guessing.
;(() => {
  try {
    if (typeof window !== 'undefined' && window.AndroidSafeArea) {
      const insets = JSON.parse(window.AndroidSafeArea.getInsets())
      const r = document.documentElement.style
      r.setProperty('--sat', insets.top + 'px')
      r.setProperty('--sab', insets.bottom + 'px')
      r.setProperty('--sal', insets.left + 'px')
      r.setProperty('--sar', insets.right + 'px')
    }
  } catch (e) { /* ignore — web build or interface not yet attached */ }
})()

// Configure native status bar on iOS and Android.
// On Android: overlaysWebView:false (also set in capacitor.config.json) pushes the
// WebView below the status bar so it never overlaps. Background colour matches the
// app's blue header.
// On iOS: overlaysWebView stays true and layout is handled by env(safe-area-inset-top)
// via the .safe-top CSS class on every header.
;(async () => {
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar')
    const { Capacitor } = await import('@capacitor/core')
    if (!Capacitor.isNativePlatform()) return

    const platform = Capacitor.getPlatform()

    if (platform === 'android') {
      // overlay:false tells the OS to push the WebView below the status bar.
      // On API < 35 (e.g. Android 8.1) the OS honours this — no overlap, no CSS vars needed.
      // On API 35+ the OS forces edge-to-edge and ignores this setting; the Java
      // onResume() injects the real inset heights as --sat/--sab CSS vars instead.
      await StatusBar.setOverlaysWebView({ overlay: false })
      await StatusBar.setBackgroundColor({ color: '#2563eb' })
      await StatusBar.setStyle({ style: Style.Light })
    } else if (platform === 'ios') {
      // Let the WebView extend under the notch/Dynamic Island;
      // spacing is handled by env(safe-area-inset-top) in CSS.
      await StatusBar.setOverlaysWebView({ overlay: true })
      await StatusBar.setStyle({ style: Style.Light })
    }
  } catch {
    // Web build or plugin unavailable — no-op
  }
})()

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
)
